import { useState, useEffect, useCallback } from "react";

const BOOKS_KEY = "reading_books_v1";

interface Book {
  id: string;
  title: string;
  author: string;
  totalPages: number | null;
  currentPage: number;
  status: "reading" | "completed";
  addedDate: string;
  completedDate?: string;
  notes: string;
}

function loadBooks(): Book[] {
  try {
    const raw = localStorage.getItem(BOOKS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Book[];
    // backfill notes field for older saved books
    return parsed.map((b) => ({ notes: "", ...b }));
  } catch {
    return [];
  }
}

function persistBooks(books: Book[]) {
  localStorage.setItem(BOOKS_KEY, JSON.stringify(books));
}

function progressPct(book: Book): number {
  if (!book.totalPages || book.totalPages === 0) return 0;
  return Math.min(100, Math.round((book.currentPage / book.totalPages) * 100));
}

interface AddFormState {
  title: string;
  author: string;
  totalPages: string;
}

const emptyForm: AddFormState = { title: "", author: "", totalPages: "" };

export default function BookShelf() {
  const [books, setBooks] = useState<Book[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState<AddFormState>(emptyForm);
  const [editingPageId, setEditingPageId] = useState<string | null>(null);
  const [pageInput, setPageInput] = useState<string>("");
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [noteInput, setNoteInput] = useState<string>("");

  useEffect(() => {
    setBooks(loadBooks());
  }, []);

  const save = useCallback((updated: Book[]) => {
    persistBooks(updated);
    setBooks(updated);
  }, []);

  const addBook = useCallback(() => {
    if (!form.title.trim()) return;
    const newBook: Book = {
      id: `book_${Date.now()}`,
      title: form.title.trim(),
      author: form.author.trim(),
      totalPages: form.totalPages ? parseInt(form.totalPages, 10) : null,
      currentPage: 0,
      status: "reading",
      addedDate: new Date().toISOString().split("T")[0],
      notes: "",
    };
    save([...books, newBook]);
    setForm(emptyForm);
    setShowAddForm(false);
  }, [books, form, save]);

  const updatePage = useCallback(
    (id: string) => {
      const val = parseInt(pageInput, 10);
      if (isNaN(val) || val < 0) return;
      save(
        books.map((b) =>
          b.id === id ? { ...b, currentPage: val } : b
        )
      );
      setEditingPageId(null);
      setPageInput("");
    },
    [books, pageInput, save]
  );

  const markComplete = useCallback(
    (id: string) => {
      save(
        books.map((b) =>
          b.id === id
            ? { ...b, status: "completed", completedDate: new Date().toISOString().split("T")[0] }
            : b
        )
      );
    },
    [books, save]
  );

  const removeBook = useCallback(
    (id: string) => {
      save(books.filter((b) => b.id !== id));
    },
    [books, save]
  );

  const openNote = useCallback((book: Book) => {
    setEditingNoteId(book.id);
    setNoteInput(book.notes);
  }, []);

  const saveNote = useCallback(
    (id: string) => {
      save(books.map((b) => (b.id === id ? { ...b, notes: noteInput } : b)));
      setEditingNoteId(null);
      setNoteInput("");
    },
    [books, noteInput, save]
  );

  const reading = books.filter((b) => b.status === "reading");
  const completed = books.filter((b) => b.status === "completed");

  return (
    <section className="px-8 lg:px-24 pb-16">
      <div className="max-w-4xl mx-auto">
        {/* Section header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <span className="text-[#3A5544] text-xs font-semibold uppercase tracking-widest mb-1 block">Reading List</span>
            <h2 className="font-serif text-2xl text-[#1A1A1A]">What are you reading?</h2>
          </div>
          {!showAddForm && (
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-2 bg-[#3A5544] text-white text-xs font-medium px-4 py-2.5 rounded hover:bg-[#2D4238] transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-add-line" />
              </div>
              Add a book
            </button>
          )}
        </div>

        {/* Add form */}
        {showAddForm && (
          <div className="bg-white rounded-lg p-6 mb-6 border border-[#E8E5E0]">
            <p className="text-[#1A1A1A] text-sm font-medium mb-4">Add a book to your list</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-[#888] mb-1.5">Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Book title"
                  className="w-full bg-[#F4F3F0] border border-[#E0DDD8] rounded px-3 py-2.5 text-sm text-[#1A1A1A] placeholder-[#CCC] focus:outline-none focus:border-[#3A5544] transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-[#888] mb-1.5">Author</label>
                <input
                  type="text"
                  value={form.author}
                  onChange={(e) => setForm({ ...form, author: e.target.value })}
                  placeholder="Author name"
                  className="w-full bg-[#F4F3F0] border border-[#E0DDD8] rounded px-3 py-2.5 text-sm text-[#1A1A1A] placeholder-[#CCC] focus:outline-none focus:border-[#3A5544] transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-[#888] mb-1.5">Total pages</label>
                <input
                  type="number"
                  min="1"
                  value={form.totalPages}
                  onChange={(e) => setForm({ ...form, totalPages: e.target.value })}
                  placeholder="e.g. 320"
                  className="w-full bg-[#F4F3F0] border border-[#E0DDD8] rounded px-3 py-2.5 text-sm text-[#1A1A1A] placeholder-[#CCC] focus:outline-none focus:border-[#3A5544] transition-colors"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={addBook}
                disabled={!form.title.trim()}
                className="bg-[#3A5544] text-white text-sm px-5 py-2.5 rounded font-medium hover:bg-[#2D4238] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add to list
              </button>
              <button
                onClick={() => { setShowAddForm(false); setForm(emptyForm); }}
                className="text-[#888] text-sm cursor-pointer hover:text-[#333] whitespace-nowrap"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Currently reading */}
        {reading.length > 0 && (
          <div className="space-y-3 mb-8">
            {reading.map((book) => (
              <div key={book.id} className="bg-white rounded-lg p-5">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <p className="text-[#1A1A1A] font-medium text-sm">{book.title}</p>
                    {book.author && <p className="text-[#888] text-xs mt-0.5">{book.author}</p>}
                  </div>
                  <button
                    onClick={() => removeBook(book.id)}
                    className="w-6 h-6 flex items-center justify-center text-[#CCC] hover:text-[#999] transition-colors cursor-pointer flex-shrink-0"
                  >
                    <i className="ri-close-line text-base" />
                  </button>
                </div>

                {/* Progress bar */}
                {book.totalPages && (
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[#AAA] text-xs">Page {book.currentPage} of {book.totalPages}</span>
                      <span className="text-[#3A5544] text-xs font-semibold">{progressPct(book)}%</span>
                    </div>
                    <div className="h-1.5 bg-[#F4F3F0] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#3A5544] rounded-full transition-all duration-300"
                        style={{ width: `${progressPct(book)}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Page update + actions */}
                <div className="flex items-center gap-3 flex-wrap">
                  {editingPageId === book.id ? (
                    <>
                      <input
                        type="number"
                        min="0"
                        max={book.totalPages ?? undefined}
                        value={pageInput}
                        onChange={(e) => setPageInput(e.target.value)}
                        placeholder="Current page"
                        className="bg-[#F4F3F0] border border-[#E0DDD8] rounded px-3 py-1.5 text-sm text-[#1A1A1A] w-32 focus:outline-none focus:border-[#3A5544] transition-colors"
                        autoFocus
                      />
                      <button
                        onClick={() => updatePage(book.id)}
                        className="text-[#3A5544] text-xs font-semibold cursor-pointer whitespace-nowrap hover:underline"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => { setEditingPageId(null); setPageInput(""); }}
                        className="text-[#AAA] text-xs cursor-pointer whitespace-nowrap"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => { setEditingPageId(book.id); setPageInput(String(book.currentPage)); }}
                        className="text-[#555] text-xs cursor-pointer hover:text-[#3A5544] transition-colors whitespace-nowrap flex items-center gap-1"
                      >
                        <i className="ri-edit-line text-xs" /> Update page
                      </button>
                      <span className="text-[#E0DDD8] text-xs">|</span>
                      <button
                        onClick={() => markComplete(book.id)}
                        className="text-[#555] text-xs cursor-pointer hover:text-[#3A5544] transition-colors whitespace-nowrap flex items-center gap-1"
                      >
                        <i className="ri-checkbox-circle-line text-xs" /> Mark finished
                      </button>
                    </>
                  )}
                </div>

                {/* Notes section */}
                <NoteBlock
                  book={book}
                  isEditing={editingNoteId === book.id}
                  noteInput={noteInput}
                  onOpen={() => openNote(book)}
                  onNoteChange={setNoteInput}
                  onSave={() => saveNote(book.id)}
                  onCancel={() => { setEditingNoteId(null); setNoteInput(""); }}
                />
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {reading.length === 0 && !showAddForm && (
          <div className="bg-white rounded-lg p-10 text-center mb-8">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 bg-[#F4F3F0] rounded-full">
              <i className="ri-book-open-line text-xl text-[#3A5544]" />
            </div>
            <p className="text-[#555] text-sm mb-1">No books added yet.</p>
            <p className="text-[#AAA] text-xs">Add the book you&apos;re currently reading to track your progress.</p>
          </div>
        )}

        {/* Completed books */}
        {completed.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-[#888] mb-4">
              Finished this challenge — {completed.length} {completed.length === 1 ? "book" : "books"}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {completed.map((book) => (
                <div key={book.id} className="bg-white rounded-lg p-4">
                  <div className="flex items-center justify-between gap-4 mb-2">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 bg-[#3A5544]/10 rounded-full">
                        <i className="ri-check-line text-[#3A5544] text-sm" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[#1A1A1A] text-sm font-medium truncate">{book.title}</p>
                        {book.author && <p className="text-[#888] text-xs">{book.author}</p>}
                      </div>
                    </div>
                    <button
                      onClick={() => removeBook(book.id)}
                      className="w-5 h-5 flex items-center justify-center text-[#CCC] hover:text-[#999] transition-colors cursor-pointer flex-shrink-0"
                    >
                      <i className="ri-close-line text-sm" />
                    </button>
                  </div>
                  <NoteBlock
                    book={book}
                    isEditing={editingNoteId === book.id}
                    noteInput={noteInput}
                    onOpen={() => openNote(book)}
                    onNoteChange={setNoteInput}
                    onSave={() => saveNote(book.id)}
                    onCancel={() => { setEditingNoteId(null); setNoteInput(""); }}
                    compact
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/* ─── NoteBlock sub-component ─── */

interface NoteBlockProps {
  book: Book;
  isEditing: boolean;
  noteInput: string;
  onOpen: () => void;
  onNoteChange: (val: string) => void;
  onSave: () => void;
  onCancel: () => void;
  compact?: boolean;
}

function NoteBlock({ book, isEditing, noteInput, onOpen, onNoteChange, onSave, onCancel, compact }: NoteBlockProps) {
  if (isEditing) {
    return (
      <div className={`${compact ? "mt-2" : "mt-4 border-t border-[#F4F3F0] pt-4"}`}>
        <textarea
          value={noteInput}
          onChange={(e) => onNoteChange(e.target.value)}
          placeholder="Jot a thought, quote, or anything that stood out..."
          rows={3}
          maxLength={500}
          className="w-full bg-[#F9F8F6] border border-[#E0DDD8] rounded px-3 py-2.5 text-sm text-[#1A1A1A] placeholder-[#CCC] focus:outline-none focus:border-[#3A5544] transition-colors resize-none leading-relaxed"
          autoFocus
        />
        <div className="flex items-center justify-between mt-2">
          <span className="text-[#CCC] text-xs">{noteInput.length}/500</span>
          <div className="flex items-center gap-3">
            <button onClick={onCancel} className="text-[#AAA] text-xs cursor-pointer whitespace-nowrap hover:text-[#555] transition-colors">
              Cancel
            </button>
            <button
              onClick={onSave}
              className="text-[#3A5544] text-xs font-semibold cursor-pointer whitespace-nowrap hover:underline"
            >
              Save note
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (book.notes) {
    return (
      <div className={`${compact ? "mt-2" : "mt-4 border-t border-[#F4F3F0] pt-4"}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2 min-w-0">
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-double-quotes-l text-[#3A5544]/40 text-sm" />
            </div>
            <p className="text-[#555] text-xs leading-relaxed italic">{book.notes}</p>
          </div>
          <button
            onClick={onOpen}
            className="text-[#CCC] hover:text-[#888] transition-colors cursor-pointer flex-shrink-0 w-4 h-4 flex items-center justify-center"
            title="Edit note"
          >
            <i className="ri-edit-line text-xs" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${compact ? "mt-2" : "mt-4 border-t border-[#F4F3F0] pt-3"}`}>
      <button
        onClick={onOpen}
        className="flex items-center gap-1.5 text-[#BBB] text-xs cursor-pointer hover:text-[#3A5544] transition-colors group"
      >
        <div className="w-3.5 h-3.5 flex items-center justify-center">
          <i className="ri-sticky-note-line text-xs" />
        </div>
        <span>Add a note or quote</span>
      </button>
    </div>
  );
}
